import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import isTouchDevice from "@insite/client-framework/Common/Utilities/isTouchDevice";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { BrandStateContext, getBrandsDataView } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import loadBrands from "@insite/client-framework/Store/Data/Brands/Handlers/LoadBrands";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ProductCarouselProductStyles } from "@insite/content-library/Components/ProductCarouselProduct";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import FlexItem, { FlexItemProps } from "@insite/content-library/Widgets/Brand/FlexItem";
import FlexWrapContainer, { FlexWrapContainerProps } from "@insite/content-library/Widgets/Brand/FlexWrapContainer";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import useInterval from "@sunSource/Components/EmblaAutoPlayInterval";
import EmblaCarousel from "@sunSource/Components/EmblaCarousal";
import ChevronLeft from "@sunSource/Icons/ChevronLeft";
import ChevronRight from "@sunSource/Icons/ChevronRight";
import useEmblaCarousel from "embla-carousel-react";
import React, { FC } from "react";

import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

const enum fields {
    autoPlaySpeed = "autoPlaySpeed",
    checkboxFieldValue = "checkboxFieldValue",
    title = "title",
    brandsToDisplay = "brandsToDisplay",
    numberOfBrandsToDisplay = "numberOfBrandsToDisplay",
    brandsSelected = "brandsSelected",
    maximumNumberOfBrandsPerRow = "maximumNumberOfBrandsPerRow",
    maximumBrandImageHeight = "maximumBrandImageHeight",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.checkboxFieldValue]: boolean;
        [fields.title]: string;
        [fields.brandsToDisplay]: "random" | "select-brands";
        [fields.numberOfBrandsToDisplay]: string;
        [fields.autoPlaySpeed]: number;
        [fields.brandsSelected]: string[];
        [fields.maximumNumberOfBrandsPerRow]: string;
        [fields.maximumBrandImageHeight]: number;
    };
}

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    ThemeProps<BaseTheme> &
    HasShellContext;

const mapStateToProps = (state: ApplicationState, props: OwnProps) => {
    const brandGalleryParameter = {
        brandIds: props.fields.brandsSelected,
        randomize: props.fields.brandsToDisplay === "random",
        pageSize: parseInt(props.fields.numberOfBrandsToDisplay || "0", 10),
        select: "id,name,detailPagePath,logoSmallImagePath,logoAltText",
    };
    const brandsState = getBrandsDataView(state, brandGalleryParameter);
    const shouldLoadBrandGallery = !brandsState.isLoading && !brandsState.value;

    return {
        brandsState,
        shouldLoadBrandGallery,
        brandGalleryParameter,
    };
};

const mapDispatchToProps = {
    loadBrandGallery: loadBrands,
};

export interface BrandListStyles {
    titleText?: TypographyProps;
    infoCardTitle?: TypographyProps;
    container?: InjectableCss;
    wrapper?: InjectableCss;
    imageContainer?: FlexWrapContainerProps;
    imageItem?: FlexItemProps;
    link?: LinkProps;
    image?: LazyImageProps;
    skipToContent?: SkipNavStyles;
    viewMoreLinkItem?: InjectableCss;
    errorMessageText?: TypographyPresentationProps;
    skipCarouselButton?: SkipNavStyles;
    mainContainer?: GridContainerProps;
    prevArrowGridItem?: GridItemProps;
    prevArrowButton?: ButtonPresentationProps;
    nextArrowGridItem?: GridItemProps;
    nextArrowButton?: ButtonPresentationProps;
    carouselGridItem?: GridItemProps;
    carouselWrapper?: InjectableCss;
    carouselContainer?: InjectableCss;

    carouselSlidesContainer?: InjectableCss;
    carouselSlide?: InjectableCss;
    carouselSlideInner?: InjectableCss;
    carouselProductStyles?: ProductCarouselProductStyles;
    mainWrapper?: InjectableCss;
}

export const listStyles: BrandListStyles = {
    infoCardTitle: {
        variant: "h2",
        css: css`
            text-align: center;
            text-transform: uppercase;
            font-size: 2rem;
            margin: 30px 0 10px;
            color: ${getColor("primary.main")};
        `,
    },
    container: {
        css: css`
            margin: 20px 0 20px 0;
            text-align: center;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            padding: 0 1rem;
                        `,
                        null,
                        null,
                        css`
                            padding: 0 5rem;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    wrapper: {
        css: css`
            margin: 0px auto 0;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            max-width: 100% !important;
                            padding: 20px;
                            flex-wrap: wrap;
                        `,
                        css`
                            max-width: 100% !important;
                            padding: 20px;
                            flex-wrap: wrap;
                        `,
                        css`
                            max-width: 720px !important;
                            flex-wrap: wrap;
                        `,
                        css`
                            max-width: 960px !important;
                            padding: 0px;
                            flex-wrap: nowrap;
                        `,
                        css`
                            max-width: 1300px !important;
                            flex-wrap: nowrap;
                            padding: 0px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    titleText: {
        variant: "h2",
        transform: "inherit",
        css: css`
            color: ${getColor("text.accent")};
            border-width: 20px;
            text-align: center;
            margin-bottom: 30px;
        `,
    },
    imageItem: {
        css: css`
            padding: 5px;
            justify-content: center;
            display: flex;
        `,
    },
    viewMoreLinkItem: {
        css: css`
            margin-top: 30px;
        `,
    },
    mainWrapper: {
        css: css`
            /* ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        css`
                            padding: 5px 35px 5px 35px;
                        `,
                        null,
                        null,
                        css`
                            padding: 0px 95px 0px 95px;
                        `,
                    ],
                    "min",
                );
            }} */
        `,
    },
    errorMessageText: {
        color: "danger",
        css: css`
            margin-top: 20px;
        `,
    },

    mainContainer: {
        gap: 0,
        css: css`
            position: relative;
        `,
    },
    prevArrowGridItem: {
        width: 1,
        align: "middle",
        css: css`
            position: absolute;
            top: 45%;
            border-radius: 50%;
            background: transparent;
            left: -9px;
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1;
            max-width: none;
            width: 40px;
            height: 40px;
        `,
    },
    prevArrowButton: {
        variant: "secondary",
        color: "text.main",
        css: css`
            span {
                width: 30px;
                height: 30px;
            }
            svg {
                width: 30px;
                height: 30px;
            }
            border: 0;
            padding: 0;
            &:focus {
                outline: none;
            }
        `,
    },
    nextArrowGridItem: {
        width: 1,
        align: "middle",
        css: css`
            position: absolute;
            top: 45%;
            right: -9px;
            border-radius: 50%;
            background: transparent;
            display: flex;
            align-items: center;
            justify-content: center;
            max-width: none;
            width: 40px;
            height: 40px;
        `,
    },
    nextArrowButton: {
        variant: "secondary",
        color: "text.main",
        css: css`
            border: 0;
            padding: 0;
            span {
                width: 30px;
                height: 30px;
            }
            svg {
                //stroke: white;
                width: 30px;
                height: 30px;
            }
            &:focus {
                outline: none;
            }
        `,
    },
    carouselGridItem: {
        width: 10,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                ])}
        `,
    },
    carouselWrapper: {
        css: css`
            width: 100%;
            position: relative;
        `,
    },
    carouselContainer: {
        css: css`
            overflow: hidden;
            padding: 20px 30px 0;
        `,
    },
    carouselSlidesContainer: {
        css: css`
            display: flex;
            padding-top: 10px;
            padding-bottom: 10px;
        `,
    },
    carouselSlide: {
        css: css`
            background: transparent;
            padding: 0;
            position: relative;
            flex: 0 0 auto;
            border-radius: 12px;
            padding: 0 10px;
        `,
    },
    carouselSlideInner: {
        css: css`
            [data-test-selector="productImage"] {
                min-height: 150px;
                max-height: 150px;
                height: 150px;
            }
            width: 100%;
            height: 100%;
            position: relative;
            padding: 15px 20px;
            display: flex;
            align-items: center;
            flex-direction: column;
            [data-test-selector="productImage"] ~ div {
                margin: 15px 0 7px 0;
                [class^="TypographyStyle"] {
                    font-size: 14px;
                }
            }
            [data-test-selector="productDescriptionLink"] > span {
                font-size: 15px;
                text-align: center;
                color: ${getColor("text.accent")};
                font-weight: 500;
                &:hover {
                    color: ${getColor("text.accent")};
                }
            }
        `,
    },
    carouselProductStyles: {
        productBrandStyles: {
            nameLink: {
                css: css`
                    [class^="TypographyStyle"] {
                        font-weight: 400 !important;
                    }
                `,
            },
        },
        productImageStyles: {
            linkWrappingImage: {
                css: css`
                    display: flex;
                    justify-content: center;
                `,
            },
            img: {
                css: css`
                    max-width: 100%;
                    max-height: 150px;
                    width: 150px;
                `,
            },
        },
    },
};

const styles = listStyles;

const BrandCarouselHomepage: FC<Props> = ({
    fields,
    shellContext,
    theme,
    brandGalleryParameter,
    loadBrandGallery,
    shouldLoadBrandGallery,
    brandsState: { isLoading, value: brandList },
}) => {
    const [emblaRef, embla] = useEmblaCarousel();
    const [windowResizeTime, setWindowResizeTime] = React.useState(0);
    const [brandsData, setBrandsdata] = React.useState<any>();
    const [canScrollPrev, setCanScrollPrev] = React.useState(false);
    const [canScrollNext, setCanScrollNext] = React.useState(false);
    const [selectedIndex, setSelectedIndex] = React.useState(0);
    const [scrollSnaps, setScrollSnaps] = React.useState([]);
    const [delay, setDelay] = React.useState(fields.autoPlaySpeed);
    const [pauseAutoplay, setPauseAutoPlay] = React.useState(100000000);
    const [isRunning, setIsRunning] = React.useState(fields.checkboxFieldValue);
    const scrollTo = React.useCallback((index: number) => embla?.scrollTo(index), [embla]);
    const scrollPrev = React.useCallback(() => embla?.scrollPrev(), [embla]);
    const scrollNext = React.useCallback(() => embla?.scrollNext(), [embla]);

    if (isRunning && typeof document !== "undefined") {
        document.addEventListener("visibilitychange", function (event) {
            if (document.hidden) {
                setDelay(pauseAutoplay);
            } else {
                setDelay(fields.autoPlaySpeed);
            }
        });
    }

    if (isRunning) {
        useInterval(
            () => {
                if (selectedIndex === scrollSnaps.length - 1) {
                    scrollTo(0);
                } else {
                    scrollNext();
                }
            },
            isRunning ? delay : 0,
        );
    }

    const arrowPause = () => {
        setDelay(pauseAutoplay);
        setTimeout(() => {
            setDelay(fields.autoPlaySpeed);
        }, 3000);
    };

    const setCanScroll = () => {
        if (shellContext.isInShell) {
            return;
        }
        setCanScrollPrev(!!embla && embla.canScrollPrev());
        setCanScrollNext(!!embla && embla.canScrollNext());
    };

    React.useEffect(() => {
        if (shouldLoadBrandGallery) {
            loadBrandGallery(brandGalleryParameter);
        }
        setBrandsdata(brandList);
    }, []);

    const getSlidesToScroll = React.useCallback(() => {
        const maxNumberOfColumns = Number(fields.maximumNumberOfBrandsPerRow) || 4;
        if (typeof window === "undefined") {
            return maxNumberOfColumns;
        }

        let localSlidesToScroll: number;
        if (window.innerWidth < theme.breakpoints.values[1]) {
            localSlidesToScroll = 1;
        } else if (window.innerWidth < theme.breakpoints.values[2]) {
            localSlidesToScroll = 2;
        } else if (window.innerWidth < theme.breakpoints.values[3]) {
            localSlidesToScroll = 3;
        } else if (window.innerWidth < theme.breakpoints.values[4]) {
            localSlidesToScroll = 4;
        } else {
            localSlidesToScroll = 5;
        }
        return Math.min(localSlidesToScroll, maxNumberOfColumns);
    }, [fields.maximumNumberOfBrandsPerRow]);

    const [slidesToScroll, setSlidesToScroll] = React.useState(getSlidesToScroll());

    const getDraggable = () => {
        if (typeof window === "undefined") {
            return false;
        }

        return window.innerWidth < theme.breakpoints.values[1];
    };

    const [draggable, setDraggable] = React.useState(getDraggable());

    React.useEffect(() => {
        const newSlidesToScroll = getSlidesToScroll();
        setSlidesToScroll(newSlidesToScroll);

        const newDraggable = getDraggable();
        setDraggable(newDraggable);
    }, [fields.maximumNumberOfBrandsPerRow, windowResizeTime]);

    React.useEffect(() => {
        if (!embla || !brandList) {
            return;
        }

        embla.on("init", setCanScroll);
        embla.on("select", setCanScroll);

        const onWindowResize = () => {
            setWindowResizeTime(Date.now());
        };

        window.addEventListener("resize", onWindowResize);
        onWindowResize();

        return () => {
            window.removeEventListener("resize", onWindowResize);
        };
    }, [embla]);

    React.useEffect(() => {
        if (!embla || !brandList) {
            return;
        }

        embla.reInit({
            align: "start",
            slidesToScroll: 1,
            watchDrag: isTouchDevice(),
            loop: brandList.length > slidesToScroll,
        });
        setCanScroll();
    }, [embla, slidesToScroll, draggable, isRunning]);

    const imgProps = {
        style: {
            width: "auto",
            maxWidth: "100%",
            maxHeight: `${fields.maximumBrandImageHeight}px`,
        },
    };

    if (!brandList) {
        return null;
    }

    return (
        <StyledWrapper {...styles.mainWrapper}>
            <StyledWrapper {...styles.wrapper}>
                {fields.title ? <Typography {...styles.infoCardTitle}>{fields.title}</Typography> : null}

                <GridContainer {...styles.mainContainer}>
                    {brandList.length > Number(fields.maximumNumberOfBrandsPerRow) && (
                        <GridItem
                            {...styles.prevArrowGridItem}
                            onMouseEnter={arrowPause}
                            onMouseLeave={() => setDelay(fields.autoPlaySpeed)}
                            onClick={arrowPause}
                        >
                            <Button
                                {...styles.prevArrowButton}
                                onClick={() => embla && embla.scrollPrev()}
                                disabled={!canScrollPrev}
                                data-test-selector="prevBtn"
                            >
                                <ButtonIcon src={ChevronLeft} />
                            </Button>
                        </GridItem>
                    )}

                    <GridItem
                        {...styles.carouselGridItem}
                        onMouseEnter={() => setDelay(pauseAutoplay)}
                        onMouseLeave={() => setDelay(fields.autoPlaySpeed)}
                    >
                        <StyledWrapper {...styles.carouselWrapper}>
                            <StyledWrapper {...styles.carouselContainer} ref={emblaRef}>
                                <StyledWrapper {...styles.carouselSlidesContainer} data-test-selector="slides">
                                    {brandList.map((brand: any, index: number) => (
                                        <StyledWrapper
                                            {...styles.carouselSlide}
                                            // eslint-disable-next-line react/no-array-index-key
                                            key={`${brand.name}${index}`}
                                            style={{ width: `calc(100% / ${slidesToScroll})` }}
                                        >
                                            <StyledWrapper
                                                {...styles.carouselSlideInner}
                                                data-test-selector="carousel_slide"
                                            >
                                                <Clickable href={brand.detailPagePath} {...styles.link}>
                                                    <LazyImage
                                                        imgProps={imgProps}
                                                        {...styles.image}
                                                        src={brand.logoSmallImagePath}
                                                        altText={`${translate("Go to brand page")} ${
                                                            brand.logoAltText || brand.name
                                                        }`}
                                                    />
                                                </Clickable>
                                            </StyledWrapper>
                                        </StyledWrapper>
                                    ))}
                                </StyledWrapper>
                            </StyledWrapper>
                        </StyledWrapper>
                    </GridItem>

                    {brandList.length > Number(fields.maximumNumberOfBrandsPerRow) && (
                        <GridItem
                            {...styles.nextArrowGridItem}
                            onMouseEnter={arrowPause}
                            onMouseLeave={() => setDelay(fields.autoPlaySpeed)}
                            onClick={arrowPause}
                        >
                            <Button
                                {...styles.nextArrowButton}
                                onClick={() => embla && embla.scrollNext()}
                                disabled={!canScrollNext}
                                data-test-selector="nextBtn"
                            >
                                <ButtonIcon src={ChevronRight} />
                            </Button>
                        </GridItem>
                    )}
                </GridContainer>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withIsInShell(withTheme(BrandCarouselHomepage))),
    definition: {
        group: "Brands",
        icon: "image",
        displayName: "Brand Carousel homepage",
        allowedContexts: ["BrandsPage", "HomePage"],
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: fields.brandsToDisplay,
                displayName: "Brands to display",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Random",
                        value: "random",
                    },
                    {
                        displayName: "Select brands",
                        value: "select-brands",
                    },
                ],
                defaultValue: "random",
                fieldType: "General",
                tooltip:
                    "Brand logos can be picked at random on page load, or you can select specific brands to display.",
            },
            {
                name: fields.numberOfBrandsToDisplay,
                displayName: "Number of brands to display",
                editorTemplate: "DropDownField",
                defaultValue: "12",
                options: [
                    { displayName: "1", value: "1" },
                    { displayName: "2", value: "2" },
                    { displayName: "3", value: "3" },
                    { displayName: "4", value: "4" },
                    { displayName: "5", value: "5" },
                    { displayName: "6", value: "6" },
                    { displayName: "7", value: "7" },
                    { displayName: "8", value: "8" },
                    { displayName: "9", value: "9" },
                    { displayName: "10", value: "10" },
                    { displayName: "11", value: "11" },
                    { displayName: "12", value: "12" },
                    { displayName: "13", value: "13" },
                    { displayName: "14", value: "14" },
                    { displayName: "15", value: "15" },
                    { displayName: "16", value: "16" },
                    { displayName: "17", value: "17" },
                    { displayName: "18", value: "18" },
                ],
                fieldType: "General",
                isVisible: item => item.fields.brandsToDisplay === "random",
            },
            {
                name: fields.brandsSelected,
                displayName: "Select brands",
                editorTemplate: "SelectBrandsField",
                isVisible: item => item.fields.brandsToDisplay === "select-brands",
                defaultValue: [],
                fieldType: "General",
            },
            {
                name: fields.maximumNumberOfBrandsPerRow,
                displayName: "Maximum brands per row",
                editorTemplate: "DropDownField",
                defaultValue: "5",
                options: [
                    { displayName: "1", value: "1" },
                    { displayName: "2", value: "2" },
                    { displayName: "3", value: "3" },
                    { displayName: "4", value: "4" },
                    { displayName: "5", value: "5" },
                ],
                fieldType: "General",
                tooltip:
                    "Up to 6 brands can display per row. This number should be adjusted based on the number of brands you choose to display. For example, if you display 10 brands you likely want 5 to display per row. Logos will also stack responsively for smaller screen sizes.",
            },
            {
                name: fields.checkboxFieldValue,
                displayName: "AutoPlay",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                isEnabled: item => true,
            },
            {
                name: fields.autoPlaySpeed,
                displayName: "Auto Play Speed",
                editorTemplate: "DropDownField",
                defaultValue: "3000",
                options: [
                    { displayName: "1000", value: "1000" },
                    { displayName: "2000", value: "2000" },
                    { displayName: "3000", value: "3000" },
                    { displayName: "4000", value: "4000" },
                    { displayName: "5000", value: "5000" },
                    { displayName: "6000", value: "6000" },
                    { displayName: "7000", value: "7000" },
                    { displayName: "8000", value: "8000" },
                    { displayName: "9000", value: "9000" },
                    { displayName: "10000", value: "10000" },
                ],
                fieldType: "General",
                isVisible: items => items.fields.checkboxFieldValue,
            },
            {
                name: fields.maximumBrandImageHeight,
                displayName: "Maximum brand image height",
                editorTemplate: "IntegerField",
                defaultValue: 100,
                fieldType: "General",
                tooltip:
                    "By default, images will scale to meet the width of the screen for the number of brands to display per row. The maximum height value can be used to scale brand logos to be smaller. Default value is 100 pixels.",
            },
        ],
    },
};

export default widgetModule;
